import { jsPDF } from 'jspdf';
import { getActionLogoPng } from '@/utils/imageUtils';
import { formatDate } from '../utils/dateUtils';
import { Chart } from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { parseISO } from 'date-fns';

// Goal type color mapping - matching Goals.vue styling
const goalTypeColors = {
    1: { bg: '#e8f5e9', text: '#4caf50' },  // Family
    2: { bg: '#f3e5f5', text: '#9c27b0' },  // Health
    3: { bg: '#e3f2fd', text: '#2196f3' },  // Company/Work
    4: { bg: '#fff3e0', text: '#ff9800' },  // Friends
    5: { bg: '#ffebee', text: '#f44336' },  // Leisure/Interest/Relaxation
    6: { bg: '#e8eaf6', text: '#3f51b5' }   // Personal Growth and Development
};

const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 102, g: 102, b: 102 };  // Default gray if conversion fails
};

const loadFirebaseImage = async (url) => {
    // Create circular mask for profile photos
    const createCircularImage = (imgData) => {
        const canvas = document.createElement('canvas');
        const size = Math.min(imgData.width, imgData.height);
        canvas.width = size;
        canvas.height = size;
        
        const ctx = canvas.getContext('2d');
        
        // Fill entire canvas with white first
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        
        // Create circular clipping path
        ctx.beginPath();
        ctx.arc(size/2, size/2, size/2, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.clip();
        
        // Draw the image
        const aspectRatio = imgData.width / imgData.height;
        let drawWidth = size;
        let drawHeight = size;
        let offsetX = 0;
        let offsetY = 0;
        
        if (aspectRatio > 1) {
            drawWidth = size * aspectRatio;
            offsetX = -(drawWidth - size) / 2;
        } else {
            drawHeight = size / aspectRatio;
            offsetY = -(drawHeight - size) / 2;
        }
        
        ctx.drawImage(imgData, offsetX, offsetY, drawWidth, drawHeight);
        
        return canvas.toDataURL('image/jpeg');
    };

    return new Promise((resolve, reject) => {
        const img = document.createElement('img');
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            // If this is a profile photo, make it circular
            const dataUrl = url.includes('profile-photos') ? 
                createCircularImage(img) : 
                canvas.toDataURL('image/jpeg');
            resolve(dataUrl);
        };

        img.onerror = (error) => {
            reject(error);
        };

        const proxyUrl = `${process.env.VUE_APP_API_URL}/proxy-image/fetch?url=${encodeURIComponent(url)}`;
        img.src = proxyUrl;
    });
};

const addPageHeader = (doc, userName) => {
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(userName, 20, 20);
    doc.line(20, 25, doc.internal.pageSize.width - 20, 25);
};

const addPageFooter = (doc, pageNumber) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    
    // Add generation date
    const generatedAt = `Generated at ${formatDate(new Date())}`;
    doc.text(generatedAt, 20, pageHeight - 20);
    
    // Add page number
    doc.text(`Page ${pageNumber}`, pageWidth - 40, pageHeight - 20);
};

export const generateGoalsPDF = async (goals, userData, selectedYear, goalTypes) => {
    const doc = new jsPDF();
    let pageNumber = 1;
    
    // Get PNG version of logo with higher resolution
    const logoPng = await getActionLogoPng(400, 160);
    
    // Cover Page
    await addCoverPage(doc, userData, selectedYear, logoPng);
    pageNumber++;
    
    // Goals Pages
    for (const goal of goals) {
        doc.addPage();
        await addGoalPage(doc, goal, pageNumber, `${userData.first_name} ${userData.last_name}`, goalTypes);
        pageNumber++;
    }
    
    // Save the PDF
    const fileName = `${userData.first_name}-${userData.last_name}-Goals-${selectedYear}.pdf`;
    doc.save(fileName);
};

const addCoverPage = async (doc, userData, year, logoPng) => {
    const topMargin = 20;
    const sideMargin = 20;
    const maxLogoHeight = 30;
    
    // Add Action Network logo
    const actionLogoAspectRatio = 2.5;
    const actionLogoHeight = maxLogoHeight;
    const actionLogoWidth = actionLogoHeight * actionLogoAspectRatio;
    
    doc.addImage(logoPng, 'PNG',
        sideMargin,
        topMargin,
        actionLogoWidth,
        actionLogoHeight
    );
    
    // Add company logo
    if (userData.company_logo_url) {
        try {
            const dataUrl = await loadFirebaseImage(userData.company_logo_url);
            
            const tempImg = new Image();
            await new Promise(resolve => {
                tempImg.onload = resolve;
                tempImg.src = dataUrl;
            });
            
            const companyLogoAspectRatio = tempImg.width / tempImg.height;
            const companyLogoHeight = maxLogoHeight;
            const companyLogoWidth = companyLogoHeight * companyLogoAspectRatio;
            
            doc.addImage(dataUrl, 'JPEG',
                doc.internal.pageSize.width - sideMargin - companyLogoWidth,
                topMargin,
                companyLogoWidth,
                companyLogoHeight
            );
        } catch (error) {
            console.error('Error adding company logo:', error);
        }
    }
    
    // Add user photo
    if (userData.photo_url) {
        try {
            const photoSize = 50;
            const dataUrl = await loadFirebaseImage(userData.photo_url);
            
            doc.addImage(dataUrl, 'JPEG',
                (doc.internal.pageSize.width - photoSize) / 2,
                120,
                photoSize,
                photoSize
            );
        } catch (error) {
            console.error('Error adding user photo:', error);
        }
    }
    
    // Adjust all other vertical positions to account for new logo placement
    doc.setFontSize(24);
    doc.setTextColor(0, 0, 0);
    const titleText = `Goals for ${year}`;
    const titleX = doc.internal.pageSize.width / 2;
    doc.text(titleText, titleX, 200, { align: 'center' });
    
    doc.setFontSize(16);
    const fullName = `${userData.first_name} ${userData.last_name}`;
    doc.text(fullName, titleX, 220, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(100, 100, 100);
    if (userData.company_name) {
        doc.text(userData.company_name, titleX, 240, { align: 'center' });
    }
    
    // Add generation date at bottom (without page number)
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    const generatedAt = `Generated at ${formatDate(new Date())}`;
    doc.text(generatedAt, 20, pageHeight - 20);
};

const formatShortDate = (date) => {
    const d = new Date(date);
    // Get first letter uppercase, rest lowercase
    const month = d.toLocaleString('default', { month: 'short' })
        .split('')
        .map((char, i) => i === 0 ? char.toUpperCase() : char.toLowerCase())
        .join('');
    const day = d.getDate();
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
};

const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const createGoalChart = (goal, width, height) => {
    const canvas = document.createElement('canvas');
    // Reduce to 2x resolution - still sharp but smaller file size
    canvas.width = width * 2;
    canvas.height = height * 2;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    
    // Sort updates chronologically
    const sortedUpdates = [...goal.updates].sort((a, b) => 
        new Date(a.update_date) - new Date(b.update_date)
    );
    
    // Create data points
    const dataPoints = sortedUpdates.map(update => ({
        x: parseISO(update.update_date),
        y: update.current_value
    }));
    
    new Chart(ctx, {
        type: 'line',
        plugins: [{
            beforeDraw: (chart) => {
                const ctx = chart.ctx;
                ctx.save();
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, chart.width, chart.height);
                ctx.restore();
            }
        }],
        data: {
            datasets: [
                {
                    label: 'Progress',
                    data: dataPoints,
                    borderColor: '#4285f4',
                    tension: 0.4,
                    fill: false,
                    borderWidth: 1
                },
                {
                    label: 'Target',
                    data: [
                        { x: dataPoints[0]?.x || parseISO(goal.target_date), y: goal.target_value },
                        { x: parseISO(goal.target_date), y: goal.target_value }
                    ],
                    borderColor: '#ea4335',
                    borderDash: [5, 5],
                    tension: 0,
                    fill: false,
                    borderWidth: 1
                }
            ]
        },
        options: {
            responsive: false,
            animation: false,
            devicePixelRatio: 4,
            backgroundColor: 'white',
            scales: {
                x: {
                    type: 'time',
                    time: { unit: 'day' },
                    grid: {
                        color: '#dddddd',
                        lineWidth: 0.5
                    },
                    ticks: {
                        color: '#666666',
                        font: {
                            size: 10
                        }
                    }
                },
                y: {
                    beginAtZero: true,
                    suggestedMax: goal.target_value * 1.1,
                    grid: {
                        color: '#dddddd',
                        lineWidth: 0.5
                    },
                    ticks: {
                        color: '#666666',
                        font: {
                            size: 10
                        }
                    }
                }
            },
            plugins: {
                legend: {
                    labels: {
                        color: '#666666',
                        font: {
                            size: 10
                        }
                    }
                }
            }
        }
    });
    
    return canvas.toDataURL('image/jpeg', 0.8);
};

const addGoalPage = async (doc, goal, pageNumber, userName, goalTypes) => {
    // Add header with name and line
    addPageHeader(doc, userName);
    
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    
    // Title section
    const titleY = 50;
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(goal.title, margin, titleY - 10);
    
    // Small type badge
    doc.setFontSize(8);
    const goalType = goalTypes.find(type => type.id === goal.goal_type_id);
    const typeName = goalType ? goalType.name : 'Other';
    const typeWidth = doc.getTextWidth(typeName) + 16;  // Add padding for sides
    const typeHeight = 8;
    const cornerRadius = 4;
    
    // Get colors for this goal type
    const typeStyle = goalTypeColors[goal.goal_type_id] || { bg: '#f5f5f5', text: '#666666' };
    
    // Convert hex colors to RGB for PDF
    const bgColor = hexToRgb(typeStyle.bg);
    doc.setFillColor(bgColor.r, bgColor.g, bgColor.b);
    doc.roundedRect(pageWidth - margin - typeWidth, titleY - 13, typeWidth, typeHeight, cornerRadius, cornerRadius, 'F');
    const textColor = hexToRgb(typeStyle.text);
    doc.setTextColor(textColor.r, textColor.g, textColor.b);
    doc.text(typeName, pageWidth - margin - typeWidth/2, titleY - 8, { align: 'center' });
    
    // Description
    doc.setFontSize(12);
    doc.setTextColor(70, 70, 70);
    const splitDescription = doc.splitTextToSize(goal.description, contentWidth);
    doc.text(splitDescription, margin, titleY);
    
    // Calculate position for target info (after description)
    const targetY = titleY + (splitDescription.length * 12) + 2;
    doc.setFontSize(12);
    
    // Target Value - Label (bold)
    doc.setFont(undefined, 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Target Value:', margin, targetY);
    
    // Target Value - Value (normal)
    const targetValueWidth = doc.getTextWidth('Target Value:');
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(goal.target_value?.toString() || '0', margin + targetValueWidth + 10, targetY);
    
    // Target Date - Label (bold)
    const pageCenter = (pageWidth - margin * 2) / 2;
    doc.setFont(undefined, 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Target Date:', margin + pageCenter, targetY);
    
    // Target Date - Value (normal)
    const targetDateWidth = doc.getTextWidth('Target Date:');
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(formatShortDate(goal.target_date), margin + pageCenter + targetDateWidth + 10, targetY);
    
    // Add horizontal line after target info
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, targetY + 8, pageWidth - margin, targetY + 8);
    
    // Chart section
    try {
        const chartCanvas = createGoalChart(goal, contentWidth, 80);
        doc.addImage(chartCanvas, 'JPEG', margin, targetY + 15, contentWidth, 80);
    } catch (error) {
        console.error('Error adding chart:', error);
    }
    
    // Progress Updates section
    const updatesY = targetY + 105;
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Progress Updates:', margin, updatesY);
    
    if (goal.updates && goal.updates.length > 0) {
        let currentY = updatesY + 12;
        for (const update of goal.updates) {
            const percentage = Math.round((update.current_value / goal.target_value) * 100);
            const updateText = `${formatShortDate(update.update_date)}: ${formatNumber(update.current_value)} (${percentage}%) - ${update.notes}`;
            const splitUpdate = doc.splitTextToSize(updateText, contentWidth);
            
            if (currentY + (splitUpdate.length * 12) > doc.internal.pageSize.height - 40) {
                doc.addPage();
                pageNumber++;
                currentY = 40;
                addPageHeader(doc, userName);
            }
            
            doc.setFontSize(10);
            doc.text(splitUpdate, margin, currentY);
            currentY += splitUpdate.length * 6 + 2;
        }
    } else {
        doc.setFontSize(10);
        doc.text('No updates recorded', margin, updatesY + 15);
    }
    
    addPageFooter(doc, pageNumber);
}; 